import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { apartmentsApi, type Building } from '../../api/apartmentsApi';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import {
  parkingManagementApi,
  type AccessCard,
  type AccessCardActionLog,
  type AccessCardKind,
  type AccessCardStockStatusFilter,
  type VehicleCardType,
} from '../../api/parkingManagementApi';
import { feeCategoriesApi, type FeeCategory } from '../../api/feeCategoriesApi';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { CardImportModal } from '../../components/apartments/CardImportModal';
import { useFunctionPermissions } from '../../hooks/useFunctionPermissions';
import ImportFileLink from '../../components/common/ImportFileLink';
import { BulkListPagination } from '../../components/bulk-fee/BulkListPagination';
import { HISTORY_PAGE_SIZE } from '../../api/paginatedHistory';
import { isParkingFeeCode } from '../../utils/manualParkingInvoiceGuard';

/** Loại thẻ filter: cards with no vehicleCardTypeId */
const VEHICLE_CARD_TYPE_FILTER_UNSET = '__unset__';

type ElevatorLinkedFilter = '' | 'linked' | 'not_linked';

type TabKind = 'VEHICLE' | 'ELEVATOR' | 'ENTRY' | 'CARD_TYPES' | 'HISTORY';

const BUILDING_LIST_FETCH_LIMIT = 100_000;

function matchesAccessCardSearch(card: AccessCard, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;
  return (
    (card.cardNumber?.toLowerCase().includes(q) ?? false) ||
    (card.cardCode?.toLowerCase().includes(q) ?? false) ||
    (card.vehicleCardType?.name?.toLowerCase().includes(q) ?? false)
  );
}

const CardManagement: React.FC = () => {
  const { t } = useLang();
  const { can } = useFunctionPermissions();
  const canImportCards = can('CARD_MANAGEMENT_IMPORT', 'import');
  const { user, isSuperAdmin, isOperator } = useAuth();
  const canBulkDelete = isSuperAdmin || isOperator;
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [activeTab, setActiveTab] = useState<TabKind>('VEHICLE');
  const [allCards, setAllCards] = useState<AccessCard[]>([]);
  const [vehicleCardTypes, setVehicleCardTypes] = useState<VehicleCardType[]>([]);
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [actionLogs, setActionLogs] = useState<AccessCardActionLog[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCard, setEditingCard] = useState<AccessCard | null>(null);
  const [showTypeFormModal, setShowTypeFormModal] = useState(false);
  const [editingType, setEditingType] = useState<VehicleCardType | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [stockStatusFilter, setStockStatusFilter] = useState<'' | AccessCardStockStatusFilter>('');
  const [vehicleCardTypeFilter, setVehicleCardTypeFilter] = useState('');
  const [elevatorLinkedFilter, setElevatorLinkedFilter] = useState<ElevatorLinkedFilter>('');
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [restoreLogId, setRestoreLogId] = useState<string | null>(null);
  const [combinedElevatorVehicleCards, setCombinedElevatorVehicleCards] = useState(false);

  const [form, setForm] = useState({
    cardNumber: '',
    cardCode: '',
    vehicleCardTypeId: '',
    notes: '',
  });

  const [typeForm, setTypeForm] = useState({
    name: '',
    code: '',
    feeCategoryId: '',
  });

  const isCardTab = activeTab === 'VEHICLE' || activeTab === 'ELEVATOR' || activeTab === 'ENTRY';
  const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId);

  useEffect(() => {
    setCombinedElevatorVehicleCards(false);
    setElevatorLinkedFilter('');
  }, [selectedBuildingId]);

  useEffect(() => {
    if (combinedElevatorVehicleCards && activeTab === 'ELEVATOR') {
      setActiveTab('VEHICLE');
    }
  }, [combinedElevatorVehicleCards, activeTab]);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedCardIds([]);
  }, [searchInput, activeTab, selectedBuildingId, stockStatusFilter, vehicleCardTypeFilter, elevatorLinkedFilter]);

  useEffect(() => {
    setHistoryPage(1);
  }, [activeTab, selectedBuildingId]);

  const filteredCards = useMemo(() => {
    let rows = allCards.filter((card) => matchesAccessCardSearch(card, searchInput));
    if (vehicleCardTypeFilter === VEHICLE_CARD_TYPE_FILTER_UNSET) {
      rows = rows.filter((card) => !card.vehicleCardTypeId);
    }
    if (combinedElevatorVehicleCards) {
      if (elevatorLinkedFilter === 'linked') {
        rows = rows.filter((card) => card.elevatorLinked);
      } else if (elevatorLinkedFilter === 'not_linked') {
        rows = rows.filter((card) => !card.elevatorLinked);
      }
    }
    return rows;
  }, [allCards, searchInput, vehicleCardTypeFilter, elevatorLinkedFilter, combinedElevatorVehicleCards]);

  const totalRecords = filteredCards.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage) || 1);

  const soldCount = useMemo(
    () => filteredCards.filter((card) => card.isSold || card.stockStatus === 'SOLD').length,
    [filteredCards],
  );

  const availableCount = useMemo(
    () => filteredCards.filter((card) => !card.isSold && card.stockStatus !== 'SOLD').length,
    [filteredCards],
  );

  const cards = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return filteredCards.slice(start, start + recordsPerPage);
  }, [filteredCards, currentPage, recordsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch {
      setError(t('failedToLoad'));
    }
  };

  const loadData = useCallback(async () => {
    if (!selectedBuildingId) return;
    try {
      setLoading(true);
      setError(null);

      if (activeTab === 'HISTORY') {
        const result = await parkingManagementApi.getAccessCardActionLogs(
          selectedBuildingId,
          undefined,
          { page: historyPage, limit: HISTORY_PAGE_SIZE },
        );
        setActionLogs(result.data);
        setHistoryTotal(result.meta.total);
        setHistoryTotalPages(result.meta.totalPages);
        setAllCards([]);
        setVehicleCardTypes([]);
        return;
      }

      if (activeTab === 'CARD_TYPES') {
        const [types, fees] = await Promise.all([
          parkingManagementApi.listVehicleCardTypes(selectedBuildingId),
          feeCategoriesApi.getAll(selectedBuildingId),
        ]);
        setVehicleCardTypes(types);
        setFeeCategories(fees);
        setAllCards([]);
        return;
      }

      const kind: AccessCardKind = activeTab;
      const [cardResult, types, fees] = await Promise.all([
        parkingManagementApi.listAccessCards({
          buildingId: selectedBuildingId,
          kind,
          stockStatus: stockStatusFilter || undefined,
          vehicleCardTypeId:
            activeTab === 'VEHICLE'
              && vehicleCardTypeFilter
              && vehicleCardTypeFilter !== VEHICLE_CARD_TYPE_FILTER_UNSET
              ? vehicleCardTypeFilter
              : undefined,
          page: 1,
          limit: BUILDING_LIST_FETCH_LIMIT,
        }),
        activeTab === 'VEHICLE'
          ? parkingManagementApi.listVehicleCardTypes(selectedBuildingId)
          : Promise.resolve([]),
        activeTab === 'VEHICLE'
          ? feeCategoriesApi.getAll(selectedBuildingId)
          : Promise.resolve([]),
      ]);
      setAllCards(cardResult.data);
      setCombinedElevatorVehicleCards(Boolean(cardResult.meta.combinedElevatorVehicleCards));
      setVehicleCardTypes(types);
      setFeeCategories(fees);
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoad')));
      setAllCards([]);
    } finally {
      setLoading(false);
    }
  }, [
    selectedBuildingId,
    activeTab,
    stockStatusFilter,
    vehicleCardTypeFilter,
    historyPage,
    t,
  ]);

  useEffect(() => {
    if (selectedBuildingId) {
      loadData();
    } else {
      setAllCards([]);
      setVehicleCardTypes([]);
      setActionLogs([]);
    }
  }, [selectedBuildingId, loadData]);

  const resetForm = () => {
    setForm({
      cardNumber: '',
      cardCode: '',
      vehicleCardTypeId: '',
      notes: '',
    });
    setEditingCard(null);
  };

  const resetTypeForm = () => {
    setTypeForm({ name: '', code: '', feeCategoryId: '' });
    setEditingType(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowFormModal(true);
  };

  const openCreateTypeModal = () => {
    resetTypeForm();
    setShowTypeFormModal(true);
  };

  const openEditModal = (card: AccessCard) => {
    setEditingCard(card);
    setForm({
      cardNumber: card.cardNumber,
      cardCode: card.cardCode ?? '',
      vehicleCardTypeId: card.vehicleCardTypeId ?? '',
      notes: card.notes ?? '',
    });
    setShowFormModal(true);
  };

  const openEditTypeModal = (type: VehicleCardType) => {
    setEditingType(type);
    setTypeForm({
      name: type.name,
      code: type.code,
      feeCategoryId: type.feeCategoryId ?? '',
    });
    setShowTypeFormModal(true);
  };

  const handleSave = async () => {
    if (!selectedBuildingId || !form.cardNumber.trim()) return;
    try {
      setSaving(true);
      if (editingCard) {
        await parkingManagementApi.updateAccessCard(editingCard.id, {
          cardNumber: form.cardNumber.trim(),
          cardCode: form.cardCode.trim() || null,
          vehicleCardTypeId:
            activeTab === 'VEHICLE' ? form.vehicleCardTypeId || null : null,
          notes: form.notes.trim() || null,
        });
      } else {
        await parkingManagementApi.createAccessCard({
          buildingId: selectedBuildingId,
          kind: activeTab as AccessCardKind,
          cardNumber: form.cardNumber.trim(),
          cardCode: form.cardCode.trim() || undefined,
          vehicleCardTypeId:
            activeTab === 'VEHICLE' ? form.vehicleCardTypeId || undefined : undefined,
          notes: form.notes.trim() || undefined,
        });
      }
      setShowFormModal(false);
      resetForm();
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, t('saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveType = async () => {
    if (!selectedBuildingId || !typeForm.name.trim()) return;
    try {
      setSaving(true);
      if (editingType) {
        await parkingManagementApi.updateVehicleCardType(editingType.id, {
          name: typeForm.name.trim(),
          code: typeForm.code.trim() || undefined,
          feeCategoryId: typeForm.feeCategoryId || null,
        });
      } else {
        await parkingManagementApi.createVehicleCardType({
          buildingId: selectedBuildingId,
          name: typeForm.name.trim(),
          code: typeForm.code.trim() || undefined,
          feeCategoryId: typeForm.feeCategoryId || null,
        });
      }
      setShowTypeFormModal(false);
      resetTypeForm();
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, t('saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('confirmDeleteCard'))) return;
    try {
      await parkingManagementApi.removeAccessCard(id);
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, t('deleteFailed')));
    }
  };

  const isCardDeletable = (card: AccessCard) =>
    card.stockStatus !== 'SOLD' && !card.isSold;

  const toggleCardSelection = (id: string) => {
    setSelectedCardIds((prev) =>
      prev.includes(id) ? prev.filter((cardId) => cardId !== id) : [...prev, id],
    );
  };

  const selectableCardsOnPage = cards.filter(isCardDeletable);
  const allSelectableOnPageSelected =
    selectableCardsOnPage.length > 0
    && selectableCardsOnPage.every((card) => selectedCardIds.includes(card.id));

  const toggleSelectAllOnPage = () => {
    if (allSelectableOnPageSelected) {
      const pageIds = new Set(selectableCardsOnPage.map((c) => c.id));
      setSelectedCardIds((prev) => prev.filter((id) => !pageIds.has(id)));
      return;
    }
    const merged = new Set([
      ...selectedCardIds,
      ...selectableCardsOnPage.map((c) => c.id),
    ]);
    setSelectedCardIds(Array.from(merged));
  };

  const runBulkDelete = async (options: { cardIds?: string[]; deleteAll?: boolean }) => {
    if (!selectedBuildingId || !isCardTab) return;
    try {
      setBulkActionLoading(true);
      setError(null);
      const result = await parkingManagementApi.bulkDeleteAccessCards({
        buildingId: selectedBuildingId,
        kind: activeTab as AccessCardKind,
        cardIds: options.cardIds,
        deleteAll: options.deleteAll,
        search: searchInput.trim() || undefined,
      });
      setSelectedCardIds([]);
      const summary = t('bulkDeleteAccessCardsResultSummary')
        .replace('{deleted}', result.deletedCount.toString())
        .replace('{skipped}', result.skipped.length.toString());
      if (result.skipped.length > 0) {
        const details = result.skipped
          .slice(0, 5)
          .map((item) => `${item.cardNumber}: ${item.reason}`)
          .join('; ');
        setImportResult(`${summary} ${details}`);
      } else {
        setImportResult(summary);
      }
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, t('deleteFailed')));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeleteSelected = async () => {
    if (!canBulkDelete || selectedCardIds.length === 0) return;
    const confirmMessage = t('confirmBulkDeleteAccessCards').replace(
      '{count}',
      selectedCardIds.length.toString(),
    );
    if (!window.confirm(confirmMessage)) return;
    await runBulkDelete({ cardIds: selectedCardIds });
  };

  const handleDeleteAllAvailable = async () => {
    if (!canBulkDelete) return;
    if (!window.confirm(t('confirmDeleteAllAvailableAccessCards'))) return;
    await runBulkDelete({ deleteAll: true });
  };

  const canRestoreFromLog = (log: AccessCardActionLog) => {
    if (log.action !== 'DELETE' && log.action !== 'BULK_DELETE') return false;
    const metadata = log.metadata as { restoredAt?: string } | null | undefined;
    return !metadata?.restoredAt;
  };

  const handleRestoreFromLog = async () => {
    if (!restoreLogId) return;
    try {
      setBulkActionLoading(true);
      setError(null);
      const result = await parkingManagementApi.restoreAccessCardsFromActionLog(restoreLogId);
      setRestoreLogId(null);
      setImportResult(
        t('bulkRestoreAccessCardsResultSummary').replace(
          '{restored}',
          result.restoredCount.toString(),
        ),
      );
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, t('saveFailed')));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDeleteType = async (id: string) => {
    if (!window.confirm(t('confirmDeleteCardType'))) return;
    try {
      await parkingManagementApi.removeVehicleCardType(id);
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, t('deleteFailed')));
    }
  };

  const handleImported = async (result: {
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
  }) => {
    setImportResult(
      `${t('importComplete')}: ${result.created} ${t('importCreated')}, ${result.updated} ${t('updated')}, ${result.skipped} ${t('skipped')}${
        result.errors.length ? `. ${result.errors.slice(0, 5).join('; ')}` : ''
      }`,
    );
    await loadData();
  };

  const parkingFeeCategories = feeCategories.filter((fc) => isParkingFeeCode(fc.feeCode));

  const formatCardAction = (action: AccessCardActionLog['action']) => {
    switch (action) {
      case 'CREATE':
        return t('cardActionCreate');
      case 'UPDATE':
        return t('cardActionUpdate');
      case 'DELETE':
        return t('cardActionDelete');
      case 'BULK_DELETE':
        return t('cardActionBulkDelete');
      case 'IMPORT':
        return t('cardActionImport');
      case 'RESTORE':
        return t('cardActionRestore');
      default:
        return action;
    }
  };

  const formatActionLogCardNumbers = (log: AccessCardActionLog) => {
    if (log.cardNumber?.trim()) {
      const metadata = log.metadata as
        | { deletedCards?: Array<{ cardNumber?: string }> }
        | null
        | undefined;
      if (log.action === 'BULK_DELETE' && metadata?.deletedCards?.length) {
        return metadata.deletedCards
          .map((item) => item.cardNumber)
          .filter(Boolean)
          .join(', ');
      }
      return log.cardNumber;
    }
    return '--';
  };

  const formatActionLogNotes = (log: AccessCardActionLog) => {
    if (log.notes?.trim()) {
      return log.notes;
    }
    const metadata = log.metadata as
      | { changeSummary?: string; cardNotes?: string | null }
      | null
      | undefined;
    if (metadata?.changeSummary?.trim()) {
      return metadata.changeSummary;
    }
    if (metadata?.cardNotes?.trim()) {
      return metadata.cardNotes;
    }
    return '—';
  };

  const formatCardKind = (kind?: AccessCardKind | null) => {
    switch (kind) {
      case 'VEHICLE':
        return t('vehicleCards');
      case 'ELEVATOR':
        return t('elevatorCards');
      case 'ENTRY':
        return t('entryCards');
      default:
        return '--';
    }
  };

  const renderAccessCardStockStatusCell = (card: AccessCard) => (
    <td className="px-4 py-3 text-sm">
      <span
        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          card.stockStatus === 'SOLD' || card.isSold
            ? 'bg-orange-100 text-orange-800'
            : 'bg-green-100 text-green-800'
        }`}
      >
        {card.stockStatus === 'SOLD' || card.isSold ? t('cardSold') : t('cardAvailable')}
      </span>
    </td>
  );

  const getCardActionBadgeClass = (action: AccessCardActionLog['action']) => {
    switch (action) {
      case 'CREATE':
        return 'bg-green-100 text-green-800';
      case 'UPDATE':
        return 'bg-blue-100 text-blue-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      case 'BULK_DELETE':
        return 'bg-red-100 text-red-800';
      case 'IMPORT':
        return 'bg-purple-100 text-purple-800';
      case 'RESTORE':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('cardManagement')}</h1>
        <div className="flex flex-wrap gap-2">
          {isCardTab && (
            <>
              {canImportCards && (
                <button
                  onClick={() => setShowImportModal(true)}
                  disabled={!selectedBuildingId}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {t('importExcel')}
                </button>
              )}
              <button
                onClick={() =>
                  parkingManagementApi.exportAccessCards(
                    selectedBuildingId,
                    activeTab as AccessCardKind,
                  )
                }
                disabled={!selectedBuildingId}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {t('exportExcel')}
              </button>
              <button
                onClick={openCreateModal}
                disabled={!selectedBuildingId}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {t('addCard')}
              </button>
            </>
          )}
          {activeTab === 'CARD_TYPES' && (
            <button
              onClick={openCreateTypeModal}
              disabled={!selectedBuildingId}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {t('addCardType')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">{error}</div>
      )}
      {importResult && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {importResult}
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        {shouldShowBuildingSelector(buildings) && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('selectBuilding')}</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => {
                selectBuilding(user?.id, e.target.value, setSelectedBuildingId);
                setSearchInput('');
                setCurrentPage(1);
              }}
                disabled={!needsBuildingSelection(buildings)}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">{t('selectBuildingPlaceholder')}</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveTab('VEHICLE')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'VEHICLE'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t(combinedElevatorVehicleCards ? 'vehicleAndElevatorCards' : 'vehicleCards')}
        </button>
        {!combinedElevatorVehicleCards && (
          <button
            onClick={() => setActiveTab('ELEVATOR')}
            className={`px-6 py-3 font-medium border-b-2 ${
              activeTab === 'ELEVATOR'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('elevatorCards')}
          </button>
        )}
        <button
          onClick={() => setActiveTab('ENTRY')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'ENTRY'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('entryCards')}
        </button>
        <button
          onClick={() => setActiveTab('CARD_TYPES')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'CARD_TYPES'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('cardTypeManagement')}
        </button>
        <button
          onClick={() => setActiveTab('HISTORY')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'HISTORY'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('cardActionHistory')}
        </button>
      </div>

      {!selectedBuildingId ? (
        <div className="text-center py-12 text-gray-500">{t('pleaseSelectBuilding')}</div>
      ) : loading ? (
        <div className="text-center py-12">{t('loading')}</div>
      ) : activeTab === 'CARD_TYPES' ? (
        <>
          <p className="text-sm text-gray-600 mb-1">{t('cardTypeManagementHint')}</p>
          <p className="text-sm text-gray-500 mb-4">{t('cardTypeUsageCountHint')}</p>
          <div className="overflow-x-auto bg-white rounded-lg shadow border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('cardTypeName')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('cardTypeCode')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('cardTypeFeeMapping')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('usageCount')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {vehicleCardTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{type.name}</td>
                    <td className="px-4 py-3 text-sm">{type.code}</td>
                    <td className="px-4 py-3 text-sm">
                      {type.feeCategory
                        ? `${type.feeCategory.name} (${type.feeCategory.feeCode ?? '-'})`
                        : t('notMapped')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {type._count?.accessCards ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm space-x-2">
                      <button
                        onClick={() => openEditTypeModal(type)}
                        className="text-blue-600 hover:underline"
                      >
                        {t('edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteType(type.id)}
                        className="text-red-600 hover:underline"
                      >
                        {t('delete')}
                      </button>
                    </td>
                  </tr>
                ))}
                {vehicleCardTypes.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      {t('noCardTypesYet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : activeTab === 'HISTORY' ? (
        <div className="overflow-x-auto bg-white rounded-lg shadow border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('exportTime')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('cardKindLabel')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('actions')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('cardNumber')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('cardCode')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('notes')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('importHistoryFileName')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('cardActionStaff')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {actionLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    {new Date(log.createdAt).toLocaleString('vi-VN')}
                  </td>
                  <td className="px-4 py-3 text-sm">{formatCardKind(log.kind)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCardActionBadgeClass(log.action)}`}
                    >
                      {formatCardAction(log.action)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm max-w-xs break-words">
                    {formatActionLogCardNumbers(log)}
                  </td>
                  <td className="px-4 py-3 text-sm">{log.cardCode ?? '--'}</td>
                  <td className="px-4 py-3 text-sm">{formatActionLogNotes(log)}</td>
                  <td className="px-4 py-3 text-sm">
                    <ImportFileLink fileName={log.fileName} fileUrl={log.fileUrl} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {log.creator?.name || log.creator?.email || '--'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {canRestoreFromLog(log) ? (
                      <button
                        type="button"
                        onClick={() => setRestoreLogId(log.id)}
                        disabled={bulkActionLoading}
                        className="text-teal-600 hover:underline disabled:opacity-50"
                      >
                        {t('restoreAccessCard')}
                      </button>
                    ) : (
                      '--'
                    )}
                  </td>
                </tr>
              ))}
              {actionLogs.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    {t('noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <BulkListPagination
            page={historyPage}
            totalPages={historyTotalPages}
            total={historyTotal}
            onPageChange={setHistoryPage}
            t={t}
          />
        </div>
      ) : (
        <>
          {activeTab === 'VEHICLE' && vehicleCardTypes.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4 text-sm">
              {t('noCardTypesForVehicleImport')}
            </div>
          )}
          <div className="mb-4 flex flex-col gap-4">
            <div className="flex flex-nowrap items-center gap-3 w-full overflow-x-auto pb-1">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('searchCardsPlaceholder')}
                className="min-w-[12rem] flex-1 max-w-md border border-gray-300 rounded-lg px-4 py-2 shrink"
              />
              <select
                value={stockStatusFilter}
                onChange={(e) =>
                  setStockStatusFilter(e.target.value as '' | AccessCardStockStatusFilter)
                }
                className="shrink-0 border border-gray-300 rounded-lg px-3 py-2 min-w-[10.5rem]"
                aria-label={t('cardStockStatus')}
              >
                <option value="">{t('cardStockStatusFilterAll')}</option>
                <option value="AVAILABLE">{t('cardAvailable')}</option>
                <option value="SOLD">{t('cardSold')}</option>
              </select>
              {activeTab === 'VEHICLE' && (
                <select
                  value={vehicleCardTypeFilter}
                  onChange={(e) => setVehicleCardTypeFilter(e.target.value)}
                  className="shrink-0 border border-gray-300 rounded-lg px-3 py-2 min-w-[10.5rem] max-w-[14rem]"
                  aria-label={t('cardType')}
                >
                  <option value="">{t('cardTypeFilterAll')}</option>
                  <option value={VEHICLE_CARD_TYPE_FILTER_UNSET}>—</option>
                  {vehicleCardTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              )}
              {activeTab === 'VEHICLE' && combinedElevatorVehicleCards && (
                <select
                  value={elevatorLinkedFilter}
                  onChange={(e) =>
                    setElevatorLinkedFilter(e.target.value as ElevatorLinkedFilter)
                  }
                  className="shrink-0 border border-gray-300 rounded-lg px-3 py-2 min-w-[11rem] max-w-[15rem]"
                  aria-label={t('cardElevatorLinked')}
                >
                  <option value="">{t('cardElevatorLinkedFilterAll')}</option>
                  <option value="linked">{t('cardElevatorLinkedFilterLinked')}</option>
                  <option value="not_linked">{t('cardElevatorLinkedFilterNotLinked')}</option>
                </select>
              )}
              {canBulkDelete && (
                <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">
                  {selectedCardIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleBulkDeleteSelected}
                      disabled={bulkActionLoading}
                      className="px-4 py-2 whitespace-nowrap bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {bulkActionLoading
                        ? t('processing')
                        : `${t('deleteSelectedCards')} (${selectedCardIds.length})`}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleDeleteAllAvailable}
                    disabled={
                      bulkActionLoading
                      || availableCount === 0
                      || stockStatusFilter === 'SOLD'
                    }
                    className="px-4 py-2 whitespace-nowrap bg-red-100 text-red-800 border border-red-200 rounded-lg hover:bg-red-200 disabled:opacity-50"
                  >
                    {t('deleteAllAvailableCards')}
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
              <span>
                {t('totalRecords')}: {totalRecords}
                {isCardTab && (
                  <>
                    {' · '}
                    {t('cardsSoldCount')}: {soldCount}
                    {' · '}
                    {t('cardsAvailableCount')}: {availableCount}
                  </>
                )}
              </span>
              <div className="flex items-center gap-2">
                <span>{t('recordsPerPage')}:</span>
                <select
                  value={recordsPerPage}
                  onChange={(e) => {
                    setRecordsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {canBulkDelete && (
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allSelectableOnPageSelected}
                        onChange={toggleSelectAllOnPage}
                        disabled={selectableCardsOnPage.length === 0}
                        aria-label={t('deleteSelectedCards')}
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('cardNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('cardCode')}
                  </th>
                  {activeTab === 'VEHICLE' && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('cardType')}
                    </th>
                  )}
                  {activeTab === 'VEHICLE' && combinedElevatorVehicleCards ? (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('cardStockStatus')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('cardElevatorLinked')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('cardVehicleAssigned')}
                      </th>
                    </>
                  ) : (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('cardStockStatus')}
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('notes')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cards.map((card) => (
                  <tr key={card.id} className="hover:bg-gray-50">
                    {canBulkDelete && (
                      <td className="px-4 py-3 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedCardIds.includes(card.id)}
                          onChange={() => toggleCardSelection(card.id)}
                          disabled={!isCardDeletable(card)}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm">{card.cardNumber}</td>
                    <td className="px-4 py-3 text-sm">{card.cardCode ?? '-'}</td>
                    {activeTab === 'VEHICLE' && (
                      <td className="px-4 py-3 text-sm">{card.vehicleCardType?.name ?? '-'}</td>
                    )}
                    {activeTab === 'VEHICLE' && combinedElevatorVehicleCards ? (
                      <>
                        {renderAccessCardStockStatusCell(card)}
                        <td className="px-4 py-3 text-sm">
                          {card.elevatorLinked ? (
                            <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                              <span aria-hidden>●</span>
                              {t('cardElevatorLinkedYes')}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {card.vehicleAssigned ? (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              {t('cardVehicleAssigned')}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </>
                    ) : (
                      renderAccessCardStockStatusCell(card)
                    )}
                    <td className="px-4 py-3 text-sm">{card.notes ?? '-'}</td>
                    <td className="px-4 py-3 text-sm space-x-2">
                      <button onClick={() => openEditModal(card)} className="text-blue-600 hover:underline">
                        {t('edit')}
                      </button>
                      {isCardDeletable(card) ? (
                        <button
                          onClick={() => handleDelete(card.id)}
                          className="text-red-600 hover:underline"
                        >
                          {t('delete')}
                        </button>
                      ) : (
                        <span className="text-gray-400">{t('delete')}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {cards.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        (activeTab === 'VEHICLE'
                          ? combinedElevatorVehicleCards
                            ? 7
                            : 6
                          : 5) + (canBulkDelete ? 1 : 0)
                      }
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {t('noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                {t('previous')}
              </button>
              <span className="px-3 py-1">
                {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                {t('next')}
              </button>
            </div>
          )}
        </>
      )}

      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingCard ? t('editCard') : t('addCard')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('cardNumber')}</label>
                <input
                  value={form.cardNumber}
                  onChange={(e) => setForm({ ...form, cardNumber: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('cardCode')}</label>
                <input
                  value={form.cardCode}
                  onChange={(e) => setForm({ ...form, cardCode: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              {activeTab === 'VEHICLE' && (
                <div>
                  <label className="block text-sm font-medium mb-1">{t('cardType')}</label>
                  <select
                    value={form.vehicleCardTypeId}
                    onChange={(e) => setForm({ ...form, vehicleCardTypeId: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="">—</option>
                    {vehicleCardTypes.length === 0 && (
                      <option value="" disabled>
                        {t('noCardTypesYet')}
                      </option>
                    )}
                    {vehicleCardTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">{t('notes')}</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowFormModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border rounded"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.cardNumber.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
              >
                {saving ? t('saving') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTypeFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingType ? t('editCardType') : t('addCardType')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('cardTypeName')}</label>
                <input
                  value={typeForm.name}
                  onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Xe máy, Ô tô, Xe đạp..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('cardTypeCode')}</label>
                <input
                  value={typeForm.code}
                  onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder={t('cardTypeCodeOptional')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('cardTypeFeeMapping')}</label>
                <select
                  value={typeForm.feeCategoryId}
                  onChange={(e) => setTypeForm({ ...typeForm, feeCategoryId: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">{t('notMapped')}</option>
                  {parkingFeeCategories.map((fc) => (
                    <option key={fc.id} value={fc.id}>
                      {fc.name} ({fc.feeCode})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">{t('cardTypeFeeMappingSaveHint')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowTypeFormModal(false);
                  resetTypeForm();
                }}
                className="px-4 py-2 border rounded"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSaveType}
                disabled={saving || !typeForm.name.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
              >
                {saving ? t('saving') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {restoreLogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <p className="text-sm text-gray-700 mb-4">{t('confirmRestoreAccessCard')}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRestoreLogId(null)}
                disabled={bulkActionLoading}
                className="px-4 py-2 border rounded"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleRestoreFromLog}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-teal-600 text-white rounded disabled:opacity-50"
              >
                {bulkActionLoading ? t('processing') : t('restoreAccessCard')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCardTab && (
        <CardImportModal
          isOpen={showImportModal}
          buildingId={selectedBuildingId}
          buildingName={selectedBuilding?.name ?? ''}
          kind={activeTab as AccessCardKind}
          t={t}
          onClose={() => setShowImportModal(false)}
          onImported={handleImported}
        />
      )}
    </div>
  );
};

export default CardManagement;
